/**
 * Metrics API Service
 *
 * Feature: 036-unified-dashboard
 * Description: Frontend API client for metrics and telemetry data
 * Constitution: v1.11.0
 */

import { apiClient } from './apiClient';

export interface SystemMetrics {
  timestamp: string;
  cpuUsagePercent: number;
  memoryUsagePercent: number;
  diskUsagePercent: number;
  networkThroughputMbps: number;
  activeConnections: number;
  requestsPerSecond: number;
  errorRate: number;
  averageLatencyMs: number;
}

export interface CapacityMetrics {
  capacityId: string;
  capacityName: string;
  capacityType: string;
  cuUsed: number;
  cuAvailable: number;
  utilizationPercent: number;
  activeWorkloads: number;
  throttlingEvents: number;
  slaCompliancePercent: number;
}

/** Spec 215 FR-005: one canonical windowed bucket (Spec-214 strict rate; null = no runs). */
export interface BackupWindowStats {
  completed: number;
  partial: number;
  failed: number;
  cancelled: number;
  successRatePct: number | null;
}

export interface BackupFamilyStats extends BackupWindowStats {
  running: number;
}

/** One day of the 30-day canonical series (gap days zero-filled, rate null). */
export interface BackupDailyPoint extends BackupWindowStats {
  date: string;
  gbMoved: number;
}

export interface BackupMetrics {
  totalBackups: number;
  completedToday: number;
  runningBackups: number;
  failedToday: number;
  /** Trailing-30d canonical strict rate (null = no finished runs in window). */
  successRate: number | null;
  averageDurationMs: number;
  totalSizeGB: number;
  storageUsedGB: number;
  /** Spec 215 FR-005/FR-006 — canonical windowed blocks + per-family breakdown. */
  today: BackupWindowStats;
  last30d: BackupWindowStats;
  byFamily: { lakehouse: BackupFamilyStats; warehouse: BackupFamilyStats; definitions: BackupFamilyStats };
  /** 30-day daily series for the Backup Status widget charts. */
  daily30d: BackupDailyPoint[];
  /** ISO timestamp of the most recent run (any family); '' when none. */
  lastBackupTime?: string;
}

export interface StorageMetrics {
  totalStorageGB: number;
  usedStorageGB: number;
  freeStorageGB: number;
  utilizationPercent: number;
  dailyGrowthGB: number;
  weeklyGrowthGB: number;
  monthlyGrowthGB: number;
  projectedFullDate: string | null;
}

export interface TimeSeriesDataPoint {
  timestamp: string;
  value: number;
  metadata?: Record<string, unknown>;
}

export interface MetricsTrend {
  metricName: string;
  currentValue: number;
  previousValue: number;
  changePercent: number;
  trend: 'increasing' | 'decreasing' | 'stable';
  dataPoints: TimeSeriesDataPoint[];
}

export interface MetricsQuery {
  metricNames?: string[];
  capacityType?: string;
  capacityId?: string;
  startTime?: string;
  endTime?: string;
  granularity?: '1m' | '5m' | '15m' | '1h' | '6h' | '1d';
  aggregation?: 'avg' | 'sum' | 'min' | 'max' | 'count';
}

const API_BASE = '/api/metrics';

class MetricsApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Route through apiClient so the MSAL bearer token + dev role / impersonation headers
    // (and 401 refresh) are attached. A raw fetch is UNAUTHENTICATED and 401s now that the
    // /api surface requires auth — that was the "Failed to load business metrics" cause.
    const method = ((options.method as string) || 'GET').toUpperCase();
    const data = options.body ? JSON.parse(options.body as string) : undefined;
    const res = await apiClient.request<T>({
      url: `${API_BASE}${endpoint}`,
      method: method as never,
      data,
      headers: (options.headers as Record<string, string>) ?? undefined,
    });
    return res.data;
  }

  /**
   * Get current system metrics
   */
  async getSystemMetrics(): Promise<SystemMetrics> {
    return this.request<SystemMetrics>('/system');
  }

  /**
   * Get system metrics history
   */
  async getSystemMetricsHistory(
    timeRange: '1h' | '6h' | '24h' | '7d' = '24h',
    granularity?: string
  ): Promise<SystemMetrics[]> {
    const params = new URLSearchParams({ timeRange });
    if (granularity) params.set('granularity', granularity);
    return this.request<SystemMetrics[]>(`/system/history?${params.toString()}`);
  }

  /**
   * Get metrics for all capacities
   */
  async getCapacityMetrics(capacityType?: string): Promise<CapacityMetrics[]> {
    const params = capacityType ? `?capacityType=${capacityType}` : '';
    return this.request<CapacityMetrics[]>(`/capacity${params}`);
  }

  /**
   * Get metrics for a specific capacity
   */
  async getCapacityMetricsById(capacityId: string): Promise<CapacityMetrics> {
    return this.request<CapacityMetrics>(`/capacity/${capacityId}`);
  }

  /**
   * Get capacity utilization trends
   */
  async getCapacityTrends(
    timeRange: '1h' | '6h' | '24h' | '7d' = '24h',
    capacityType?: string
  ): Promise<MetricsTrend> {
    const params = new URLSearchParams({ timeRange });
    if (capacityType) params.set('capacityType', capacityType);
    return this.request<MetricsTrend>(`/capacity/trends?${params.toString()}`);
  }

  /**
   * Get backup metrics summary
   */
  async getBackupMetrics(capacityType?: string): Promise<BackupMetrics> {
    const params = capacityType ? `?capacityType=${capacityType}` : '';
    return this.request<BackupMetrics>(`/backup${params}`);
  }

  /**
   * Get backup success rate trend
   */
  async getBackupTrends(
    timeRange: '7d' | '30d' | '90d' = '30d'
  ): Promise<TimeSeriesDataPoint[]> {
    return this.request<TimeSeriesDataPoint[]>(`/backup/trends?timeRange=${timeRange}`);
  }

  /**
   * Get storage metrics
   */
  async getStorageMetrics(capacityType?: string): Promise<StorageMetrics> {
    const params = capacityType ? `?capacityType=${capacityType}` : '';
    return this.request<StorageMetrics>(`/storage${params}`);
  }

  /**
   * Get storage growth history
   */
  async getStorageHistory(
    timeRange: '7d' | '30d' | '90d' = '30d'
  ): Promise<TimeSeriesDataPoint[]> {
    return this.request<TimeSeriesDataPoint[]>(`/storage/history?timeRange=${timeRange}`);
  }

  /**
   * Get maintenance health summary
   */
  async getMaintenanceHealth(capacityType?: string): Promise<{
    overallHealth: 'healthy' | 'warning' | 'critical';
    healthScore: number;
    totalLakehouses: number;
    needsMaintenance: number;
    overdueMaintenance: number;
  }> {
    const params = capacityType ? `?capacityType=${capacityType}` : '';
    return this.request(`/maintenance/health${params}`);
  }

  /**
   * Query custom metrics with flexible parameters
   */
  async queryMetrics(query: MetricsQuery): Promise<{
    metrics: Record<string, TimeSeriesDataPoint[]>;
    aggregations?: Record<string, number>;
  }> {
    return this.request('/query', {
      method: 'POST',
      body: JSON.stringify(query),
    });
  }

  /**
   * Get real-time metrics via Server-Sent Events
   */
  subscribeToMetrics(
    metricTypes: string[],
    onMetric: (metric: { type: string; data: unknown }) => void
  ): () => void {
    const params = new URLSearchParams();
    metricTypes.forEach(type => params.append('types', type));
    
    const eventSource = new EventSource(`${API_BASE}/stream?${params.toString()}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMetric(data);
      } catch (error) {
        console.error('Failed to parse metrics SSE message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('Metrics SSE error:', error);
    };

    // Return unsubscribe function
    return () => {
      eventSource.close();
    };
  }

  /**
   * Get dashboard widget data in a single call
   */
  async getDashboardWidgetData(
    widgets: string[],
    capacityType?: string
  ): Promise<Record<string, unknown>> {
    const params = new URLSearchParams();
    widgets.forEach(w => params.append('widgets', w));
    if (capacityType) params.set('capacityType', capacityType);
    return this.request(`/dashboard?${params.toString()}`);
  }

  /**
   * Get SLA compliance metrics
   */
  async getSlaMetrics(capacityType?: string): Promise<{
    overallCompliance: number;
    byCapacityType: Record<string, number>;
    violationsToday: number;
    atRiskCount: number;
  }> {
    const params = capacityType ? `?capacityType=${capacityType}` : '';
    return this.request(`/sla${params}`);
  }

  /**
   * Export metrics data
   */
  async exportMetrics(
    query: MetricsQuery,
    format: 'json' | 'csv' = 'json'
  ): Promise<Blob> {
    // apiClient so auth is attached (raw fetch was unauthenticated).
    const res = await apiClient.post<Blob>(
      `${API_BASE}/export`,
      { ...query, format },
      { responseType: 'blob', headers: { Accept: format === 'csv' ? 'text/csv' : 'application/json' } },
    );
    return res.data;
  }

  /**
   * Get backup coverage percentage (Feature 136 — T010)
   */
  async getBackupCoverage(capacityType?: string): Promise<{
    totalLakehouses: number;
    backedUpLakehouses: number;
    /** Null when there is nothing to protect (Spec 298 FR-004). */
    coveragePercent: number | null;
    lastCalculated: string;
  }> {
    const params = capacityType ? `?capacityType=${capacityType}` : '';
    return this.request(`/backup/coverage${params}`);
  }
}

export const metricsApi = new MetricsApiService();
export default metricsApi;